import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import cn from 'clsx';
import { useEffect, useRef, useState, JSX, RefObject } from 'react';

import { disableUIBlocker } from '@/components/common';
import { PayButton } from '@/components/pages/stored-instruments/utils/PayButton';
import { triggerErrorToast } from '@/libs/toasts';
import { DebitMandateDetails } from '@/schemas/DebitMandateDetails';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import PaymentStore from '@/services/payments/PaymentStore';
import {
  isValidIban,
  isValidIbanFormat,
} from '@/services/sepa/helpers/IbanValidationHelper';
import { getQaTestingProps, QaTestingElements } from '@/utils/qaTesting';

interface ISepaFormProps {
  readonly navigation?: JSX.Element | null;
}

export function SepaForm({ navigation }: ISepaFormProps) {
  const { t } = useTranslation();
  const { accessToken, userId, givenName, familyName } = useAuth();

  const [ibanValue, setIbanValue] = useState('');
  const [ibanValid, setIbanValid] = useState(false);

  const ibanFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const customerNumber = userId;
    if (customerNumber === undefined) {
      throw new Error('customerNumber is undefined!');
    }

    let iban = ibanValue?.trim();
    iban = iban.replace(/\s+/g, '').toUpperCase();
    if (iban?.length <= 0) {
      markIbanFieldInvalid(ibanFieldRef);
    } else {
      if (isValidIbanFormat(iban)) {
        markIbanFieldValid(ibanFieldRef);
      } else {
        markIbanFieldInvalid(ibanFieldRef);
      }
    }

    function markIbanFieldValid(ibanRef: RefObject<HTMLInputElement | null>) {
      setIbanValid(true);
      markFieldValid(ibanRef);
    }

    function markIbanFieldInvalid(ibanRef: RefObject<HTMLInputElement | null>) {
      setIbanValid(false);
      markFieldInvalid(ibanRef);
    }
  }, [ibanValue]);

  function markFieldInvalid(fieldRef: RefObject<HTMLInputElement | null>) {
    fieldRef.current?.classList?.remove('border-silver-700');
    fieldRef.current?.classList?.add('border-error');
  }

  function markFieldValid(fieldRef: RefObject<HTMLInputElement | null>) {
    fieldRef.current?.classList?.remove('border-error');
    fieldRef.current?.classList?.add('border-silver-700');
  }

  const defaultCustomerName =
    PaymentStore.customerName || `${givenName} ${familyName}`;

  const [customerName, setCustomerName] = useState(defaultCustomerName);

  const setMandateDetailsAndNavigateToConsent = async () => {
    const ibanIsValid = await isValidIban(ibanValue, accessToken ?? '').catch(
      () => false,
    );
    disableUIBlocker();

    if (!ibanIsValid) {
      triggerErrorToast(t('Invalid IBAN'));
      markFieldInvalid(ibanFieldRef);
      setIbanValid(false);
      return;
    }

    const sepaMandateDetails: DebitMandateDetails = {
      iban: ibanValue,
      customerName: customerName,
    };

    PaymentStore.directDebitMandate = sepaMandateDetails;
    NavigationService.navigateTo({ page: InternalPage.SepaMandateConsent });
  };

  return (
    <div>
      <div className="flex flex-col p-sm ring-1 ring-silver-700 bg-white rounded-md">
        <input
          id="accountHolderName"
          type="text"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          className="relative rounded-field bg-secondary h-14 p-sm focused-container w-full border-[1px] border-silver-700 shadow-on-focus"
          {...getQaTestingProps(
            QaTestingElements.AccountHolderName,
            customerName,
          )}
        />
        <input
          id="iban"
          placeholder="IBAN"
          type="text"
          value={ibanValue}
          onChange={(e) => setIbanValue(e.target.value)}
          className="mt-sm relative rounded-field bg-secondary h-14 p-sm w-full border-[1px] border-silver-700 shadow-on-focus"
          ref={ibanFieldRef}
          {...getQaTestingProps(QaTestingElements.AccountIban, ibanValue)}
        />
      </div>

      <div className={cn('flex mt-md space-x-2')}>
        {navigation}
        <PayButton
          onClick={() => setMandateDetailsAndNavigateToConsent()}
          title={
            PaymentStore.flow === PaymentIntentFlow.Manage
              ? t('Add new SEPA mandate')
              : t('Proceed to pay')
          }
          disabled={!ibanValid}
        />
      </div>
    </div>
  );
}
